// Admin Panel - Extender trial de usuarios (solo can_grant_subscriptions)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield, ArrowLeft, Infinity as InfinityIcon, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [trialEnd, setTrialEnd] = useState('');
  const [permanent, setPermanent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) { navigate('/login'); return; }
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok || !data.can_grant_subscriptions) {
          toast.error('No tienes permisos para acceder aqui');
          navigate('/dashboard');
          return;
        }
        setUser(data);
        setChecking(false);
        loadUsers();
      } catch {
        navigate('/login');
      }
    };
    verify();
  }, [token, navigate]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API}/api/v2/frugal/subscription/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setUsers((data.users || []).slice(-15).reverse());
    } catch { /* ignore */ }
    finally { setLoadingUsers(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Ingresa el email'); return; }
    if (!permanent && !trialEnd) { toast.error('Selecciona una fecha o marca permanente'); return; }

    setSubmitting(true);
    try {
      const body = {
        email: email.trim().toLowerCase(),
        trial_end: permanent ? null : new Date(trialEnd + 'T23:59:59').toISOString()
      };
      const res = await fetch(`${API}/api/v2/frugal/subscription/admin/extend-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Trial actualizado');
        setEmail(''); setTrialEnd(''); setPermanent(false);
        loadUsers();
      } else {
        toast.error(data.detail || 'Error al actualizar');
      }
    } catch {
      toast.error('Error de conexion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (targetEmail) => {
    if (!window.confirm(`Revocar acceso a ${targetEmail}? El usuario quedara como expirado.`)) return;
    try {
      const res = await fetch(`${API}/api/v2/frugal/subscription/admin/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email: targetEmail })
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || 'Acceso revocado'); loadUsers(); }
      else toast.error(data.detail || 'Error al revocar');
    } catch { toast.error('Error de conexion'); }
  };

  const getStatusBadge = (u) => {
    if (u.subscription_paid) return <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded">Activo pago</span>;
    const st = u.subscription_status;
    if (st === 'active') return <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded">Activo</span>;
    if (st === 'trial') {
      const end = u.trial_end ? new Date(u.trial_end) : null;
      if (end && end.getFullYear() >= 2099) return <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">Permanente</span>;
      return <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">Trial</span>;
    }
    if (st === 'expired') return <span className="text-xs bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded">Expirado</span>;
    return <span className="text-xs bg-slate-500/20 text-slate-400 px-2 py-0.5 rounded">-</span>;
  };

  if (checking) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Verificando permisos...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm" data-testid="admin-back-btn">
          <ArrowLeft size={16} /> Volver al dashboard
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Panel Admin</h1>
            <p className="text-sm text-slate-400">Extender trial o conceder acceso permanente</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800/50 rounded-2xl p-6 border border-white/10 mb-6">
          <label className="block text-sm text-slate-300 mb-2">Email del usuario</label>
          <input
            type="email" placeholder="usuario@ejemplo.com" value={email}
            onChange={(e) => setEmail(e.target.value)} required
            data-testid="admin-email-input"
            className="w-full bg-slate-700/50 text-white px-4 py-3 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />

          <label className="block text-sm text-slate-300 mb-2">Fecha de expiracion del trial</label>
          <input
            type="date" value={trialEnd} disabled={permanent}
            onChange={(e) => setTrialEnd(e.target.value)}
            data-testid="admin-date-input"
            min={new Date().toISOString().split('T')[0]}
            className="w-full bg-slate-700/50 text-white px-4 py-3 rounded-xl mb-3 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-40"
          />

          <label className="flex items-center gap-2 text-sm text-slate-300 mb-5 cursor-pointer">
            <input
              type="checkbox" checked={permanent}
              onChange={(e) => setPermanent(e.target.checked)}
              data-testid="admin-permanent-checkbox"
              className="w-4 h-4 accent-amber-500"
            />
            <InfinityIcon className="w-4 h-4 text-purple-400" />
            <span>Acceso permanente (sin fecha de expiracion)</span>
          </label>

          <button
            type="submit" disabled={submitting}
            data-testid="admin-submit-btn"
            className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            {permanent ? <InfinityIcon size={18} /> : <Calendar size={18} />}
            {submitting ? 'Procesando...' : (permanent ? 'Conceder acceso permanente' : 'Extender trial')}
          </button>
        </form>

        <div className="bg-slate-800/50 rounded-2xl p-5 border border-white/10">
          <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center justify-between">
            <span>Ultimos usuarios</span>
            <button onClick={loadUsers} className="text-xs text-amber-400 hover:text-amber-300">
              {loadingUsers ? '...' : 'Recargar'}
            </button>
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {users.length === 0 && !loadingUsers && <p className="text-slate-500 text-sm text-center py-4">Sin usuarios</p>}
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-2.5 text-sm" data-testid={`user-row-${u.email}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white truncate">{u.email}</p>
                    {u.cancel_requested && (
                      <span className="text-[10px] bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded whitespace-nowrap">
                        Solicita cancelar
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-xs truncate">{u.name || '-'}</p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {getStatusBadge(u)}
                  <button
                    onClick={() => { setEmail(u.email); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="text-xs text-amber-400 hover:text-amber-300"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleRevoke(u.email)}
                    className="text-xs text-rose-400 hover:text-rose-300"
                    data-testid={`revoke-btn-${u.email}`}
                  >
                    Revocar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
